import { useEffect } from 'react';
import Layout from '../../../components/feature/Layout';
import SchemaOrg from '../../../components/base/SchemaOrg';

export default function WaterTankCleaningPage() {
  useEffect(() => {
    const pageTitle = '水塔清洗｜專業水塔清潔消毒｜永業環保';
    const pageDesc = '永業環保提供專業水塔清洗消毒服務，使用環保署認證清潔劑，確保水質純淨安全。服務桃園、新北、台北、新竹、苗栗等地區，20年經驗，政府認證，收費透明。';
    const pageKeywords = '水塔清洗,水塔清潔,水塔消毒,水塔維護,水質檢測';
    const pageUrl = `${siteUrl}/seo/water-tank-cleaning`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);
  }, [siteUrl]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const cleaningSteps = [
    {
      step: 1,
      title: '預約檢查',
      description: '專業人員到府評估水塔狀況，確認清洗方案',
      icon: 'ri-calendar-check-line'
    },
    {
      step: 2,
      title: '抽水排空',
      description: '使用專業設備將水塔內的水完全抽乾',
      icon: 'ri-water-flash-line'
    },
    {
      step: 3,
      title: '刷洗清潔',
      description: '人工刷洗水塔內壁，清除污垢與沉澱物',
      icon: 'ri-brush-line'
    },
    {
      step: 4,
      title: '高壓沖洗',
      description: '使用高壓水柱徹底沖洗水塔各個角落',
      icon: 'ri-contrast-drop-line'
    },
    {
      step: 5,
      title: '消毒殺菌',
      description: '使用環保消毒藥劑進行全面消毒處理',
      icon: 'ri-shield-check-line'
    },
    {
      step: 6,
      title: '檢測完工',
      description: '水質檢測確認符合標準，提供清洗證明',
      icon: 'ri-file-list-3-line'
    }
  ];

  const whyClean = [
    {
      title: '法規要求',
      description: '依據《飲用水管理條例》，水塔每半年至少清洗一次',
      icon: 'ri-government-line',
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: '健康安全',
      description: '定期清洗可預防細菌、藻類滋生，保障飲用水安全',
      icon: 'ri-heart-pulse-line',
      color: 'from-red-500 to-red-600'
    },
    {
      title: '延長壽命',
      description: '清除水垢與雜質，延長水塔使用年限，減少維修成本',
      icon: 'ri-time-line',
      color: 'from-green-500 to-green-600'
    },
    {
      title: '水質改善',
      description: '清洗後水質清澈無異味，提升生活品質',
      icon: 'ri-drop-line',
      color: 'from-cyan-500 to-cyan-600'
    }
  ];

  const cleaningFrequency = [
    {
      type: '住宅水塔',
      frequency: '每6個月',
      reason: '符合法規要求，保障家人飲用水安全',
      price: '$2,500起'
    },
    {
      type: '社區大樓',
      frequency: '每3-6個月',
      reason: '使用人數多，需更頻繁清洗維護',
      price: '$5,000起'
    },
    {
      type: '商業大樓',
      frequency: '每3個月',
      reason: '符合商業衛生標準，保障員工健康',
      price: '$8,000起'
    },
    {
      type: '餐飲業',
      frequency: '每2-3個月',
      reason: '食品安全要求高，需定期檢測清洗',
      price: '$3,500起'
    }
  ];

  const regulations = [
    {
      title: '飲用水管理條例',
      content: '依據《飲用水管理條例》第12條規定，供公眾飲用之連續供水固定設備，其所有人、使用人或管理人應維護水質，每半年至少清洗水塔一次。',
      authority: '環保署'
    },
    {
      title: '建築物用水設備',
      content: '《建築技術規則》規定，建築物設置之蓄水池、水塔應便於清洗、維護及檢查，並應有防止污染之措施。',
      authority: '內政部'
    },
    {
      title: '飲用水水質標準',
      content: '清洗後的水塔水質應符合《飲用水水質標準》，包括大腸桿菌群、總菌落數、濁度、臭度等指標。',
      authority: '環保署'
    }
  ];

  const faqItems = [
    {
      question: '水塔多久需要清洗一次？',
      answer: '根據環保署建議，水塔至少每半年清洗一次。如果水塔位於空氣污染較嚴重的地區，或是水質明顯變差，建議每3-4個月清洗一次。'
    },
    {
      question: '水塔清洗需要多久時間？',
      answer: '一般住宅水塔（5噸以下）清洗時間約2-3小時，大型水塔或社區水塔可能需要4-6小時。我們會在施工前評估時間，並配合您的時間安排。'
    },
    {
      question: '清洗水塔會停水嗎？',
      answer: '是的，清洗期間需要暫時停水。我們建議您事先儲水備用，或安排在用水較少的時段進行。清洗完成後會立即恢復供水。'
    },
    {
      question: '你們使用什麼清潔劑？',
      answer: '我們使用環保署認證的食品級清潔劑和消毒劑，對人體無害，不會殘留有害物質。清洗後會徹底沖洗，確保水質安全。'
    },
    {
      question: '水塔清洗費用如何計算？',
      answer: '費用依水塔容量、高度、污染程度而定。一般住宅水塔（5噸以下）約$2,500-$3,500，大型水塔需現場評估。我們提供免費到府估價。'
    }
  ];

  const dangerSigns = [
    {
      sign: '水質混濁',
      description: '水龍頭流出的水呈現白色或黃色混濁狀',
      severity: 'high',
      icon: 'ri-drop-line'
    },
    {
      sign: '異味產生',
      description: '水有腥臭味、霉味或其他異常氣味',
      severity: 'high',
      icon: 'ri-nose-line'
    },
    {
      sign: '水中雜質',
      description: '水中出現黑色、紅色或其他顏色的雜質',
      severity: 'high',
      icon: 'ri-alert-line'
    },
    {
      sign: '水塔青苔',
      description: '水塔內壁或水面出現綠色青苔',
      severity: 'medium',
      icon: 'ri-plant-line'
    },
    {
      sign: '水垢堆積',
      description: '水塔底部有厚重的沉澱物或水垢',
      severity: 'medium',
      icon: 'ri-stack-line'
    },
    {
      sign: '超過半年',
      description: '距離上次清洗已超過6個月',
      severity: 'medium',
      icon: 'ri-calendar-line'
    }
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/seo/water-tank-cleaning#service`,
      name: '水塔清洗服務',
      description: '專業水塔清洗消毒服務，使用環保署認證清潔劑，確保水質純淨安全。提供完整清洗報告與水質檢測。',
      provider: {
        '@type': 'LocalBusiness',
        name: '永業環保',
        telephone: '0800-281-281',
        url: siteUrl
      },
      areaServed: [
        { '@type': 'City', name: '桃園市' },
        { '@type': 'City', name: '新北市' },
        { '@type': 'City', name: '台北市' },
        { '@type': 'City', name: '新竹市' },
        { '@type': 'City', name: '苗栗縣' }
      ],
      offers: {
        '@type': 'Offer',
        priceRange: 'NT$2,500-NT$5,000',
        priceCurrency: 'TWD'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/seo/water-tank-cleaning#faqpage`,
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/seo/water-tank-cleaning#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '水塔清洗', item: `${siteUrl}/seo/water-tank-cleaning` }
      ]
    }
  ];

  return (
    <Layout>
      <SchemaOrg schema={schemas} />
      <div className="pt-24">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                專業水塔清洗服務
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                符合法規標準，使用環保藥劑，徹底清潔消毒<br/>
                定期清洗水塔，保障全家飲用水安全
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="/contact" 
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  立即預約清洗
                </a>
                <a 
                  href="#cleaning-steps" 
                  className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line text-xl"></i>
                  了解清洗流程
                </a>
              </div>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-blue-600 mb-2">20+</div>
                <div className="text-gray-600">服務年資</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-blue-600 mb-2">5000+</div>
                <div className="text-gray-600">清洗案例</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
                <div className="text-gray-600">符合法規</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-blue-600 mb-2">環保</div>
                <div className="text-gray-600">認證藥劑</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Clean Water Tank */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">為什麼要定期清洗水塔？</h2>
              <p className="text-lg text-gray-600">水塔清洗不只是法規要求，更是健康保障</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyClean.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all hover:-translate-y-2 border-2 border-gray-100"
                >
                  <div className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <i className={`${item.icon} text-4xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Danger Signs */}
        <section className="py-20 bg-gradient-to-br from-red-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">水塔需要清洗的警訊</h2>
              <p className="text-lg text-gray-600">出現以下情況，請立即安排清洗</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dangerSigns.map((item, index) => (
                <div 
                  key={index}
                  className={`bg-white rounded-xl p-6 border-l-4 ${
                    item.severity === 'high' ? 'border-red-500' : 'border-orange-400'
                  } hover:shadow-lg transition-shadow`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${
                      item.severity === 'high' ? 'bg-red-100' : 'bg-orange-100'
                    } rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <i className={`${item.icon} text-2xl ${
                        item.severity === 'high' ? 'text-red-600' : 'text-orange-600'
                      }`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.sign}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                      {item.severity === 'high' && (
                        <span className="inline-block mt-2 text-xs font-semibold text-red-600 bg-red-100 px-3 py-1 rounded-full">
                          立即處理
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cleaning Steps */}
        <section id="cleaning-steps" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">專業清洗流程</h2>
              <p className="text-lg text-gray-600">六大步驟，確保水塔徹底清潔</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cleaningSteps.map((step, index) => (
                <div 
                  key={index}
                  className="relative bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {step.step}
                  </div>
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-6 shadow-md">
                    <i className={`${step.icon} text-3xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{step.title}</h3>
                  <p className="text-gray-600 text-center leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cleaning Frequency */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">建議清洗頻率</h2>
              <p className="text-lg text-gray-600">不同場所有不同的清洗需求</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cleaningFrequency.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.type}</h3>
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-blue-600 mb-2">{item.frequency}</div>
                    <div className="text-sm text-gray-500">清洗一次</div>
                  </div>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">{item.reason}</p>
                  <div className="pt-6 border-t border-gray-200">
                    <div className="text-2xl font-bold text-gray-900">{item.price}</div>
                    <div className="text-sm text-gray-500">參考價格</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-blue-100 border-l-4 border-blue-600 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-blue-600"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">價格說明</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    以上為參考價格，實際費用依水塔大小、高度、清洗難度而定。我們提供免費現場評估，報價透明，絕不額外加價。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Regulations */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">相關法規說明</h2>
              <p className="text-lg text-gray-600">了解水塔清洗的法律規範</p>
            </div>

            <div className="space-y-6 max-w-4xl mx-auto">
              {regulations.map((reg, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 border-l-4 border-blue-600"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-government-line text-2xl text-white"></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-gray-900">{reg.title}</h3>
                        <span className="text-sm font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                          {reg.authority}
                        </span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{reg.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">常見問題</h2>
              <p className="text-lg text-gray-600">關於水塔清洗的常見疑問</p>
            </div>

            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-start gap-3">
                    <i className="ri-question-line text-blue-600 text-2xl"></i>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed pl-11">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              保障飲用水安全，從定期清洗水塔開始
            </h2>
            <p className="text-xl text-blue-50 mb-8">
              符合法規標準，使用環保藥劑<br/>
              專業團隊為您提供完整的水塔清洗服務
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/contact" 
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                立即預約清洗
              </a>
              <a 
                href="/services/water-tank" 
                className="inline-flex items-center gap-2 bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line text-xl"></i>
                了解更多服務
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}